/**
 * Lo puro de las imágenes de las plantillas (fase 2): qué archivo se puede subir, cómo se lee para mandarlo y cómo se
 * dicen el peso y las medidas. El backend vuelve a mirar todo (el tipo, por los bytes): esto es para avisar antes de
 * subir 5 MB que no sirven.
 */

import type { ImagenOficial, PlantillaBorrador } from "../types";
import { siguienteNumero, sincronizarVariables } from "./variables";

/** El tope de WhatsApp para la imagen del encabezado (el mismo del backend, que lo manda en la lista). */
export const MAX_BYTES_IMAGEN = 5 * 1024 * 1024;
/** Lo que acepta el selector de archivos: sólo lo que acepta WhatsApp. */
export const ACEPTA_IMAGEN = "image/jpeg,image/png";

/** Por qué no se puede subir ese archivo, o `null` si se puede. */
export function problemaDeArchivo(f: { type: string; size: number; name: string }, maxBytes = MAX_BYTES_IMAGEN): string | null {
  const tipo = (f.type || "").toLowerCase();
  const porNombre = /\.(jpe?g|png)$/i.test(f.name || "");
  if (!["image/jpeg", "image/png", "image/jpg"].includes(tipo) && !(tipo === "" && porNombre)) {
    return "WhatsApp sólo acepta imágenes JPG o PNG. Si es otro formato (WEBP, GIF, HEIC), exportala como JPG y subila de nuevo.";
  }
  if (f.size === 0) return "El archivo está vacío.";
  if (f.size > maxBytes) return `La imagen pesa ${pesoLegible(f.size)} y WhatsApp acepta hasta ${pesoLegible(maxBytes)}. Achicala y subila otra vez.`;
  return null;
}

/** «850 KB», «1,2 MB». */
export function pesoLegible(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString("es-AR", { maximumFractionDigits: 1 })} MB`;
}

/** «1200 × 628 px», o «» si no se pudieron leer. */
export function medidasLegibles(i: Pick<ImagenOficial, "ancho" | "alto">): string {
  return i.ancho && i.alto ? `${i.ancho} × ${i.alto} px` : "";
}

/**
 * Un aviso si la imagen se va a ver mal en el teléfono, o `null`. WhatsApp la muestra a lo ancho de la burbuja: con
 * menos de 500 px de ancho se ve borrosa. No frena nada.
 */
export function avisoDeMedidas(i: Pick<ImagenOficial, "ancho" | "alto">): string | null {
  if (i.ancho && i.ancho < 500) return "Es chica: en el teléfono se va a ver borrosa. Mejor una de 800 px de ancho o más.";
  return null;
}

/** El archivo como `data:<tipo>;base64,…` (el backend acepta el prefijo). */
export function leerComoBase64(f: Blob): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => resolver(String(lector.result || ""));
    lector.onerror = () => rechazar(lector.error || new Error("No se pudo leer el archivo."));
    lector.readAsDataURL(f);
  });
}

/* ── La imagen que se elige en cada campaña (pedido de Alan del 26/10/2026) ─────────────────────────────────────── */

/** El archivo con que termina el link de una imagen de la biblioteca (el `ARCHIVO_PUBLICO_RE` del backend). */
export const ARCHIVO_IMAGEN_RE = /^[a-f0-9]{32}\.(jpg|png)$/;
/** El link de la biblioteca sin el archivo (`RUTA_PUBLICA_IMAGENES` del backend), como fuente de una RegExp. */
const PREFIJO_RE = String.raw`(https:\/\/[^\s{}]+\/api\/v1\/activa\/public\/imagenes\/)`;

/**
 * Una plantilla con la imagen que se elige en cada campaña tiene `mediaUrl` = `<link de la biblioteca>/{{N}}`: Twilio y
 * Meta aceptan una variable al final de la URL de la imagen, con un ejemplo para aprobarla (el nombre de un archivo de la
 * biblioteca). Devuelve el número de esa variable y el prefijo del link, o null si la imagen es fija.
 */
export function imagenPorCampana(mediaUrl: string | null | undefined): { numero: number; prefijo: string } | null {
  const m = new RegExp(String.raw`^${PREFIJO_RE}\{\{([1-9]\d{0,2})\}\}$`).exec(String(mediaUrl || "").trim());
  return m ? { numero: Number(m[2]), prefijo: m[1] } : null;
}

/** El link de una imagen de la biblioteca, partido en prefijo y archivo; null si no es de la biblioteca. */
export function partirLinkDeImagen(url: string | null | undefined): { prefijo: string; archivo: string } | null {
  const m = new RegExp(String.raw`^${PREFIJO_RE}([a-f0-9]{32}\.(?:jpg|png))$`).exec(String(url || "").trim());
  return m ? { prefijo: m[1], archivo: m[2] } : null;
}

/**
 * Pasa la plantilla a «la imagen se elige en cada campaña», con `linkDeEjemplo` (una imagen de la biblioteca) como la
 * que ve Meta para aprobarla. La variable es la siguiente libre (o la que ya tenía), y su ejemplo es el archivo.
 * null si el link no es de la biblioteca (una imagen de la web de la marca no se puede cambiar por campaña).
 */
export function conImagenPorCampana(b: PlantillaBorrador, linkDeEjemplo: string): PlantillaBorrador | null {
  const p = partirLinkDeImagen(linkDeEjemplo);
  if (!p) return null;
  const n = imagenPorCampana(b.mediaUrl)?.numero ?? siguienteNumero({ ...b, mediaUrl: "" });
  const conUrl: PlantillaBorrador = { ...b, mediaUrl: `${p.prefijo}{{${n}}}` };
  const variables = sincronizarVariables(conUrl).map((v) => (v.numero === n ? { ...v, alias: "Imagen de la campaña", ejemplo: p.archivo, campoSugerido: undefined } : v));
  return { ...conUrl, variables };
}

/** Vuelve a una imagen fija: la de ejemplo queda como la imagen de la plantilla. */
export function sinImagenPorCampana(b: PlantillaBorrador): PlantillaBorrador {
  const actual = imagenPorCampana(b.mediaUrl);
  if (!actual) return b;
  const ejemplo = (b.variables || []).find((v) => v.numero === actual.numero)?.ejemplo || "";
  const fija: PlantillaBorrador = { ...b, mediaUrl: ARCHIVO_IMAGEN_RE.test(ejemplo) ? actual.prefijo + ejemplo : "" };
  return { ...fija, variables: sincronizarVariables(fija) };
}
